import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from '../lib/store'
import { passkeySupported, humanizePasskeyError } from '../lib/passkey'
import { RECRUITER_ROLES } from '../lib/constants'
import type { RecruiterPrefs } from '../lib/types'
import { Avatar, Badge, Button, Card, Input, Modal, Select } from '../components/ui'
import { IconCheck, IconLogout, IconPasskey } from '../components/icons'
import './pages.css'

function Toggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      style={{
        width: 44,
        height: 26,
        borderRadius: 999,
        border: '1px solid var(--hairline-hi)',
        background: on ? 'var(--c-mid)' : 'var(--surface-2)',
        position: 'relative',
        cursor: 'pointer',
        flex: 'none',
        transition: 'background var(--t-med) var(--ease)',
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 20 : 2,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left var(--t-med) var(--ease-spring)',
        }}
      />
    </button>
  )
}

const ROLE_OPTIONS = RECRUITER_ROLES.map((r) => ({ value: r, label: r }))

function EditProfileModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { session, updateRecruiter } = useStore()
  const { recruiter } = session

  // Seed the form from the current profile each time the modal opens.
  const [name, setName] = useState(recruiter.name)
  const [company, setCompany] = useState(recruiter.company)
  const [role, setRole] = useState(recruiter.role || RECRUITER_ROLES[0])
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [seeded, setSeeded] = useState(false)

  if (open && !seeded) {
    setName(recruiter.name)
    setCompany(recruiter.company)
    setRole(recruiter.role || RECRUITER_ROLES[0])
    setError('')
    setSeeded(true)
  } else if (!open && seeded) {
    setSeeded(false)
  }

  async function submit() {
    if (!name.trim()) return setError('Add your name.')
    if (!company.trim()) return setError('Add your company.')
    setSaving(true)
    setError('')
    try {
      await updateRecruiter({ name: name.trim(), company: company.trim(), role })
      onClose()
    } catch {
      setError('Could not save — please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Edit profile">
      <div className="stack" style={{ gap: '1rem' }}>
        <Input label="Full name" placeholder="Maya Chen" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Company" placeholder="Acme Inc." value={company} onChange={(e) => setCompany(e.target.value)} />
        <Select label="Role" options={ROLE_OPTIONS} value={role} onChange={(e) => setRole(e.target.value)} />
        <Input label="Work email" value={recruiter.email} disabled hint="Your sign-in email can't be changed here." />
        {error && <span className="field__error">{error}</span>}
        <div className="row-between" style={{ marginTop: '0.25rem' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Save changes
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export function Settings() {
  const { session, logout, enrollPasskey, updateRecruiter } = useStore()
  const nav = useNavigate()
  const { recruiter } = session
  const prefs = recruiter.prefs

  const [passkeyAdded, setPasskeyAdded] = useState(false)
  const [passkeyError, setPasskeyError] = useState('')
  const [busy, setBusy] = useState(false)
  const [editOpen, setEditOpen] = useState(false)

  function togglePref(key: keyof RecruiterPrefs) {
    updateRecruiter({ prefs: { ...prefs, [key]: !prefs[key] } }).catch(() => {})
  }

  async function addPasskey() {
    setBusy(true)
    setPasskeyError('')
    try {
      await enrollPasskey()
      setPasskeyAdded(true)
    } catch (e) {
      setPasskeyError(humanizePasskeyError(e))
    } finally {
      setBusy(false)
    }
  }

  function doLogout() {
    logout()
    nav('/login')
  }

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Settings</h1>
          <p className="page__sub">Your profile, sign-in security, and console preferences.</p>
        </div>
      </div>

      <div className="stack">
        {/* Profile */}
        <Card>
          <div className="section-title">Profile</div>
          <div className="row-between" style={{ alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Avatar hue={recruiter.avatarHue} initials={recruiter.initials} size={56} />
              <div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, letterSpacing: '-0.01em' }}>{recruiter.name}</div>
                <div className="setting-row__sub">
                  {recruiter.role} · {recruiter.company}
                </div>
              </div>
            </div>
            <Button variant="secondary" onClick={() => setEditOpen(true)}>
              Edit profile
            </Button>
          </div>
          <hr className="divider" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
            <div className="kv">
              <span className="kv__k">Work email</span>
              <span className="kv__v">{recruiter.email}</span>
            </div>
            <div className="kv">
              <span className="kv__k">Company domain</span>
              <span className="kv__v mono">{recruiter.domain}</span>
            </div>
            <div className="kv">
              <span className="kv__k">Role</span>
              <span className="kv__v">{recruiter.role}</span>
            </div>
          </div>
        </Card>

        {/* Security / passkeys */}
        <Card>
          <div className="section-title">Sign-in &amp; security</div>
          <div className="setting-row">
            <div className="setting-row__text">
              <div className="setting-row__title">
                Passkey{' '}
                {passkeyAdded && (
                  <Badge color="var(--cred-live)" bg="var(--cred-live-bg)" dot>
                    Active
                  </Badge>
                )}
              </div>
              <div className="setting-row__sub">
                {passkeySupported()
                  ? 'Your highest-preference sign-in. Face ID / Touch ID / Windows Hello — no password to phish.'
                  : 'Passwordless sign-in for this account.'}
              </div>
              {passkeyError && (
                <div className="field__error" style={{ marginTop: 6 }}>
                  {passkeyError}
                </div>
              )}
            </div>
            <Button
              variant={passkeyAdded ? 'secondary' : 'primary'}
              icon={passkeyAdded ? <IconCheck size={15} /> : <IconPasskey size={16} />}
              loading={busy}
              onClick={addPasskey}
            >
              {passkeyAdded ? 'Add another' : 'Add a passkey'}
            </Button>
          </div>
          <div className="setting-row">
            <div className="setting-row__text">
              <div className="setting-row__title">Single sign-on</div>
              <div className="setting-row__sub">Connect Google or Microsoft Workspace for your team.</div>
            </div>
            <Button variant="secondary">Configure SSO</Button>
          </div>
        </Card>

        {/* Preferences */}
        <Card>
          <div className="section-title">Preferences</div>
          <div className="setting-row">
            <div className="setting-row__text">
              <div className="setting-row__title">Weekly pipeline digest</div>
              <div className="setting-row__sub">A Monday summary of new verified candidates and pending requests.</div>
            </div>
            <Toggle on={prefs.emailDigest} onToggle={() => togglePref('emailDigest')} />
          </div>
          <div className="setting-row">
            <div className="setting-row__text">
              <div className="setting-row__title">Surface verified candidates first</div>
              <div className="setting-row__sub">Default the candidate list to verification-strength order.</div>
            </div>
            <Toggle on={prefs.surfaceVerified} onToggle={() => togglePref('surfaceVerified')} />
          </div>
          <div className="setting-row">
            <div className="setting-row__text">
              <div className="setting-row__title">Live Vouch alerts</div>
              <div className="setting-row__sub">
                Notify me the moment a candidate passes a Live Vouch or completes a verification step.
              </div>
            </div>
            <Toggle on={prefs.liveAlerts} onToggle={() => togglePref('liveAlerts')} />
          </div>
        </Card>

        {/* Danger / sign out */}
        <Card>
          <div className="setting-row" style={{ paddingBottom: 0, borderBottom: 'none' }}>
            <div className="setting-row__text">
              <div className="setting-row__title">Sign out</div>
              <div className="setting-row__sub">End your session on this device.</div>
            </div>
            <Button variant="danger" icon={<IconLogout size={16} />} onClick={doLogout}>
              Sign out
            </Button>
          </div>
        </Card>
      </div>

      <EditProfileModal open={editOpen} onClose={() => setEditOpen(false)} />
    </div>
  )
}
